import {
  ExternalLink,
  Plus,
  RadioTower,
  Trash2,
} from "lucide-react";
import { useState, type FormEvent } from "react";

import {
  addSource,
  deleteSource,
  updateSource,
} from "@/lib/newsroom";
import type { NewsSource, SourceKind } from "@/lib/types";

export function SourceSettings({
  ownerId,
  sources,
  onRefresh,
}: {
  ownerId: string;
  sources: NewsSource[];
  onRefresh: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<SourceKind>("rss");
  const [value, setValue] = useState("");
  const [trustTier, setTrustTier] = useState(3);
  const [isPrimary, setIsPrimary] = useState(false);
  const [refreshMinutes, setRefreshMinutes] = useState(1);
  const [message, setMessage] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    try {
      await addSource({
        ownerId,
        name,
        kind,
        endpoint: kind === "rss" ? value : null,
        searchQuery: kind === "rss" ? null : value,
        trustTier,
        isPrimary,
        refreshMinutes,
      });
      setName("");
      setValue("");
      setTrustTier(3);
      setIsPrimary(false);
      setRefreshMinutes(1);
      await onRefresh();
      setMessage("Source added.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to add source");
    }
  }

  async function remove(sourceId: string) {
    await deleteSource(sourceId);
    await onRefresh();
  }

  return (
    <section className="settings-section">
      <header className="settings-section-header">
        <div>
          <p className="eyebrow">SOURCES</p>
          <h2>Discovery feeds</h2>
          <p>
            RSS is the low-cost baseline. Exa expands discovery, GDELT broadens
            coverage, and Firecrawl is used only when extraction is sparse.
          </p>
        </div>
      </header>

      <div className="source-list">
        {sources.map((source) => (
          <article className="source-row" key={source.id}>
            <span className={`source-kind source-${source.kind}`}>
              <RadioTower aria-hidden="true" />
              {source.kind}
            </span>
            <div className="source-main">
              <strong>{source.name}</strong>
              <span>{source.endpoint || source.search_query}</span>
              <small>
                Tier {source.trust_tier}
                {source.is_primary_source ? " · primary" : ""}
                {source.last_fetched_at
                  ? ` · last run ${new Date(source.last_fetched_at).toLocaleString()}`
                  : " · not fetched yet"}
              </small>
            </div>
            {source.endpoint ? (
              <a
                className="icon-button"
                href={source.endpoint}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${source.name}`}
              >
                <ExternalLink aria-hidden="true" />
              </a>
            ) : null}
            <label className="source-refresh">
              <span>Refresh</span>
              <select
                value={source.refresh_minutes}
                onChange={async (event) => {
                  await updateSource(source.id, {
                    refresh_minutes: Number(event.target.value),
                  });
                  await onRefresh();
                }}
              >
                <option value={1}>1 min</option>
                <option value={5}>5 min</option>
                <option value={10}>10 min</option>
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>1 hour</option>
              </select>
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={source.enabled}
                onChange={async (event) => {
                  await updateSource(source.id, {
                    enabled: event.target.checked,
                  });
                  await onRefresh();
                }}
              />
              <span />
              <b>{source.enabled ? "Enabled" : "Paused"}</b>
            </label>
            <button
              type="button"
              className="icon-button danger"
              aria-label={`Delete ${source.name}`}
              onClick={() => void remove(source.id)}
            >
              <Trash2 aria-hidden="true" />
            </button>
          </article>
        ))}
      </div>

      <form className="add-source-card" onSubmit={submit}>
        <div className="section-heading">
          <div>
            <p className="eyebrow">NEW INPUT</p>
            <h2>Add a source</h2>
          </div>
          <Plus aria-hidden="true" />
        </div>
        <div className="form-grid">
          <label>
            <span>Name</span>
            <input
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="SEC press releases"
            />
          </label>
          <label>
            <span>Source type</span>
            <select
              value={kind}
              onChange={(event) => setKind(event.target.value as SourceKind)}
            >
              <option value="rss">RSS / Atom</option>
              <option value="gdelt">GDELT query</option>
              <option value="exa">Exa query</option>
            </select>
          </label>
          <label className="wide-field">
            <span>{kind === "rss" ? "Feed URL" : "Search query"}</span>
            <input
              required
              type={kind === "rss" ? "url" : "text"}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder={
                kind === "rss"
                  ? "https://example.com/feed.xml"
                  : "crypto regulation OR bitcoin ETF"
              }
            />
          </label>
          <label>
            <span>Trust tier</span>
            <select
              value={trustTier}
              onChange={(event) => setTrustTier(Number(event.target.value))}
            >
              <option value={1}>Tier 1 · primary / official</option>
              <option value={2}>Tier 2 · established newsroom</option>
              <option value={3}>Tier 3 · discovery source</option>
              <option value={4}>Tier 4 · review required</option>
            </select>
          </label>
          <label>
            <span>Refresh interval</span>
            <select
              value={refreshMinutes}
              onChange={(event) =>
                setRefreshMinutes(Number(event.target.value))
              }
            >
              <option value={1}>Every minute</option>
              <option value={5}>Every 5 minutes</option>
              <option value={10}>Every 10 minutes</option>
              <option value={15}>Every 15 minutes</option>
              <option value={30}>Every 30 minutes</option>
              <option value={60}>Every hour</option>
            </select>
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(event) => setIsPrimary(event.target.checked)}
            />
            <span>Official primary source</span>
          </label>
        </div>
        <button type="submit" className="button-primary">
          <Plus aria-hidden="true" />
          Add source
        </button>
        {message ? <p className="inline-message">{message}</p> : null}
      </form>
    </section>
  );
}
